import { MutableRefObject, useCallback } from "react";
import * as THREE from "three";

const useOrientationControls = ({
  cameraRef,
  lonRef,
  setUseOrientation,
}: {
  cameraRef: MutableRefObject<THREE.PerspectiveCamera | null>;
  lonRef: MutableRefObject<number>;
  setUseOrientation: (value: boolean) => void;
}) => {
  const handleOrientation = useCallback(
    (event: DeviceOrientationEvent) => {
      if (!cameraRef.current) return;
      setUseOrientation(true);

      const alpha = event.alpha ? THREE.MathUtils.degToRad(event.alpha) : 0; // Z-axis rotation (alpha)
      const beta = event.beta ? THREE.MathUtils.degToRad(event.beta) : 0; // X-axis rotation (beta)
      const gamma = event.gamma ? THREE.MathUtils.degToRad(event.gamma) : 0; // Y-axis rotation (gamma)

      // Assuming alpha is the direction (0-360 degrees) the device is facing
      lonRef.current = 270 - event.alpha!; // Update lon based on alpha. Adjust sign/scale as necessary.

      // Create Euler rotation from device orientation angles
      const euler = new THREE.Euler(beta, gamma, alpha, "ZXY");

      // Convert Euler to Quaternion
      const q1 = new THREE.Quaternion().setFromEuler(euler);

      const q2 = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(1, 0, 0),
        -Math.PI / 2,
      );
      cameraRef.current.quaternion.multiplyQuaternions(q2, q1);
    },
    [cameraRef, lonRef, setUseOrientation],
  );

  const supportsOrientationControl = useCallback(() => {
    const hasOrientationEvent = "DeviceOrientationEvent" in window;
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    return hasOrientationEvent && hasTouch;
  }, []);

  const requestPermission = useCallback(() => {
    if (
      typeof window.DeviceOrientationEvent !== "undefined" &&
      typeof (window.DeviceOrientationEvent as any).requestPermission ===
        "function"
    ) {
      (window.DeviceOrientationEvent as any)
        .requestPermission()
        .then((permissionState: any) => {
          if (permissionState === "granted") {
            window.addEventListener("deviceorientation", handleOrientation);
          } else {
            alert("Permission denied");
          }
        })
        .catch(console.error);
    } else {
      // For non-iOS 13+ devices
      window.addEventListener("deviceorientation", handleOrientation);
    }
  }, [handleOrientation]);

  const disableOrientation = useCallback(() => {
    window.removeEventListener("deviceorientation", handleOrientation);
    setUseOrientation(false);
  }, [handleOrientation, setUseOrientation]);

  return {
    supportsOrientationControl,
    requestPermission,
    disableOrientation,
  };
};

export default useOrientationControls;
